import React, { useEffect, useState } from "react";
import { getData, postData, postFormData } from '../middleware/apiMiddleware';
import s from './VocabAdvDetail.module.css';
import { useNavigate } from "react-router-dom";

const ALL_POS = [
  "abbreviation", "adjective", "adverb", "conjunction", "contraction",
  "determiner", "interjection", "noun", "number", "prefix", "preposition",
  "pronoun", "verb"
];

export default function VocabAdvDetail({ word }) {
	const navigate = useNavigate();
	const [detailData, setDetailData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [editedContent, setEditedContent] = useState({});
	const [editedLemmas, setEditedLemmas] = useState({});
	const [imageUploadLoading, setImageUploadLoading] = useState({});

	useEffect(() => {
		setEditedContent({});
	}, [detailData, word]);

	useEffect(() => {
		setEditedLemmas({});
	}, [detailData, word]);

	useEffect(() => {
		if (!word) return;
		const fetchDetail = async () => {
			setLoading(true);
			setError(null);
			try {
				const res = await getData(`vocabApi/getVocabAdvDetail?word=${word}`, navigate);
				setDetailData(res.data);
			} catch (err) {
				setError("Failed to load word detail");
			} finally {
				setLoading(false);
			}
		};
		fetchDetail();
	}, [word, navigate]);

    const generalWordUpload = async (selectedFile) => {
        try {			
            const data = new FormData();
            data.append("wordId", word);
            data.append("generalfile", selectedFile);
            let res = await postFormData("utility/generalWordUpload", data, navigate);
            return "https://ehla-media-bucket.s3.amazonaws.com/" + res.data;
        } catch (err) {
            console.error("Image upload failed:", err);
            return null;
        }
    }

    // Bulk save all edited baseJson
    const handleBulkUpdate = async () => {
        if (!detailData) return;
        const updatedBaseJson = JSON.parse(JSON.stringify(detailData.baseJson));

        Object.entries(editedContent).forEach(([key, editData]) => {
            const [posType, meaningIndex] = key.split('-');
            const index = Number(meaningIndex);
            if (updatedBaseJson[posType]?.meanings?.[index]) {
                updatedBaseJson[posType].meanings[index] = {
                    ...updatedBaseJson[posType].meanings[index],
                    ...editData
                };
            }
        });

        setIsUpdating(true);
        try {
            await postData("vocabApi/updateVocabAdvDetail", {
                word: word,
                baseJson: updatedBaseJson
            }, navigate);

            setDetailData(prev => ({ ...prev, baseJson: updatedBaseJson }));
            setEditedContent({});
        } catch (err) {
            console.error("Bulk update failed:", err);
            alert("Failed to update content");
        } finally {
            setIsUpdating(false);
        }
    };

    // Remove single meaning image
    const handleRemoveMeaningImage = (key) => {
        setEditedContent(prev => ({
            ...prev,
            [key]: { ...prev[key], image: null  }
        }));

    };

	// Lemma variant update unchanged
    const handleUpdateVariant = async (variantIndex) => {
        if (!detailData || !word) return;
        const editedData = editedLemmas[variantIndex] || {};
        const originalVariant = detailData.lemmas[variantIndex];

        setIsUpdating(true);
        try {
            const finalAmeSpelling = editedData.ame_spelling?.trim() ?? originalVariant.ame_spelling ?? "";
            const finalUkIpa = editedData.ipa_en_gb?.trim() ?? originalVariant.ipa_en_gb ?? "";
            const finalUsIpa = editedData.ipa_en_us?.trim() ?? originalVariant.ipa_en_us ?? "";

            await postData(
            "vocabApi/updateVocabAdvLemma",
            {
                word: word,
                lemma_word: originalVariant.word,
                relationship: originalVariant.relationship,
                pos: originalVariant.pos,
                ame_spelling: finalAmeSpelling,
                ipa_en_gb: finalUkIpa,
                ipa_en_us: finalUsIpa
            },
            navigate
            );

            const updatedLemmas = [...detailData.lemmas];
            updatedLemmas[variantIndex] = {
            ...updatedLemmas[variantIndex],
            ame_spelling: finalAmeSpelling,
            ipa_en_gb: finalUkIpa,
            ipa_en_us: finalUsIpa
            };

            setDetailData(prev => ({ ...prev, lemmas: updatedLemmas }));
            setEditedLemmas(prev => ({ ...prev, [variantIndex]: {} }));
            alert("Word Variant Updated Successfully!");

        } catch (err) {
            console.error("Variant Update Failed:", err);
            alert("Failed to update word variant!");
        } finally {
            setIsUpdating(false);
        }
    };

	const renderPosExtraFields = (posType, posData) => {
		if (!posData) return null;
		switch (posType) {
			case "noun":
				return (
					<>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Countability:</span><span>{posData.countability || "-"}</span></div>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Plural Form:</span><span>{posData.plural_form || "-"}</span></div>
					</>
				);
			case "verb":
				return (
					<>
						<div className={s.fieldRow}><span className={s.fieldLabel}>3rd Person Singular:</span><span>{posData.third_person_singular || "-"}</span></div>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Present Participle:</span><span>{posData.present_participle || "-"}</span></div>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Past Tense:</span><span>{posData.past_tense || "-"}</span></div>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Past Participle:</span><span>{posData.past_participle || "-"}</span></div>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Transitivity:</span><span>{posData.transitivity || "-"}</span></div>
					</>
				);
			case "adjective":
				return (
					<>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Comparative:</span><span>{posData.comparative || "-"}</span></div>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Superlative:</span><span>{posData.superlative || "-"}</span></div>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Usage Type:</span><span>{posData.attributive_predicative || "-"}</span></div>
					</>
				);
			case "adverb":
				return (
					<>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Comparative:</span><span>{posData.comparative || "-"}</span></div>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Superlative:</span><span>{posData.superlative || "-"}</span></div>
					</>
				);
			case "pronoun":
				return (
					<>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Objective Form:</span><span>{posData.objective || "-"}</span></div>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Possessive Form:</span><span>{posData.possessive || "-"}</span></div>
						<div className={s.fieldRow}><span className={s.fieldLabel}>Reflexive Form:</span><span>{posData.reflexive || "-"}</span></div>
					</>
				);
			case "determiner": return <div className={s.fieldRow}><span className={s.fieldLabel}>Number Type:</span><span>{posData.numberType || "-"}</span></div>;
			case "number": return <div className={s.fieldRow}><span className={s.fieldLabel}>Ordinal Form:</span><span>{posData.ordinal || "-"}</span></div>;
			case "contraction": return <div className={s.fieldRow}><span className={s.fieldLabel}>Full Original Form:</span><span>{posData.full_form || "-"}</span></div>;
			default: return null;
		}
	};

	const renderMeanings = (posType, meaningList) => {
        if (!Array.isArray(meaningList) || meaningList.length === 0) return <p className={s.emptyTip}>No meanings available</p>;

        return meaningList.map((item, idx) => {
            const key = `${posType}-${idx}`;
            const currentEdit = editedContent[key] || {};
            const isUploading = imageUploadLoading[key];
            
            // 🔥 FIXED: Check if image is NOT removed (null = removed)
            const hasMeaningImage = currentEdit.image !== null && (!!item.image || !!currentEdit.image);
            // 🔥 FIXED: Final image source (null = no image)
            const displayImage = currentEdit.image === null ? null : (currentEdit.image || item.image);

            return (
                <div key={key} className={s.meaningItem}>
                    <h4>Meaning {idx + 1}</h4>

                    {/* 🔥 FIXED: Only show image if NOT removed */}
                    {displayImage && (
                        <div className={s.meaningImageContainer}>
                            <img
                                src={displayImage}
                                alt="Meaning illustration"
                                className={s.meaningImage}
                                onError={(e) => e.target.style.display = 'none'}
                            />
                        </div>
                    )}

                    {isEditMode && (
                        <div className={s.uploadImageRow} style={{display:"flex", gap:"10px", alignItems:"center"}}>
                            <label htmlFor={`pt-${posType}-id-${idx}`} className={s.uploadBtn}>
                                {isUploading ? "Uploading..." : "Upload Meaning Image"}
                                <input
                                    id={`pt-${posType}-id-${idx}`}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    disabled={isUploading || isUpdating}
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        setImageUploadLoading(prev => ({ ...prev, [key]: true }));
                                        const imagePath = await generalWordUpload(file);
                                        setImageUploadLoading(prev => ({ ...prev, [key]: false }));
                                        if (imagePath) {
                                            setEditedContent(prev => ({
                                                ...prev,
                                                [key]: { ...prev[key], image: imagePath }
                                            }));
                                        } else {
                                            alert("Image upload failed!");
                                        }
                                        e.target.value = "";
                                    }}
                                />
                            </label>

                            {/* Remove Image Button - uses FIXED hasMeaningImage */}
                            {hasMeaningImage && (
                                <button
                                    className={s.removeImgBtn}
                                    disabled={isUpdating || isUploading}
                                    onClick={() => handleRemoveMeaningImage(key)}
                                >
                                    Remove Image
                                </button>
                            )}
                        </div>
                    )}

                    {/* Rest of your code (meaning fields) remains UNCHANGED */}
                    {item.grammar && <p className={s.grammarNote}><strong>Grammar Note:</strong> {item.grammar}</p>}
                    <div className={s.meaningEditRow}>
                        <label className={s.fieldLabel}>English Meaning:</label>
                        {isEditMode ? (
                            <textarea
                                value={currentEdit.meaning ?? item.meaning}
                                onChange={(e) => setEditedContent(prev => ({
                                    ...prev,
                                    [key]: { ...prev[key], meaning: e.target.value }
                                }))}
                                className={s.meaningInput}
                                disabled={isUpdating}
                            />
                        ) : (
                            <p className={s.staticText}>{item.meaning}</p>
                        )}
                    </div>
                    <div className={s.meaningEditRow}>
                        <label className={s.fieldLabel}>中文釋義:</label>
                        {isEditMode ? (
                            <textarea
                                value={currentEdit.meaning_zh ?? item.meaning_zh}
                                onChange={(e) => setEditedContent(prev => ({
                                    ...prev,
                                    [key]: { ...prev[key], meaning_zh: e.target.value }
                                }))}
                                className={s.meaningInput}
                                disabled={isUpdating}
                            />
                        ) : (
                            <p className={s.staticText}>{item.meaning_zh}</p>
                        )}
                    </div>
                    <div className={s.meaningEditRow}>
                        <label className={s.fieldLabel}>Example:</label>
                        {isEditMode ? (
                            <textarea
                                value={currentEdit.example ?? item.example}
                                onChange={(e) => setEditedContent(prev => ({
                                    ...prev,
                                    [key]: { ...prev[key], example: e.target.value }
                                }))}
                                className={s.meaningInput}
                                disabled={isUpdating}
                            />
                        ) : (
                            <p className={s.staticText}>{item.example}</p>
                        )}
                    </div>
                    <div className={s.meaningEditRow}>
                        <label className={s.fieldLabel}>例句:</label>
                        {isEditMode ? (
                            <textarea
                                value={currentEdit.example_zh ?? item.example_zh}
                                onChange={(e) => setEditedContent(prev => ({
                                    ...prev,
                                    [key]: { ...prev[key], example_zh: e.target.value }
                                }))}
                                className={s.meaningInput}
                                disabled={isUpdating}
                            />
                        ) : (
                            <p className={s.staticText}>{item.example_zh}</p>
                        )}
                    </div>
                </div>
            );
        });
    };

	if (!word) return null;

    return (
    <div className={s.detailContainer}>
        {loading && <p className={s.loadText}>Loading word data...</p>}
        {error && <p className={s.errText}>{error}</p>}

        {!loading && !error && detailData && (
        <div className={s.wrapBox}>
            <div className={s.topSection}>
            <div className={s.topLeft}>
                <h2>Word Detail：<span className={s.mainWord}>{word}</span></h2>
                
                <div>
                    <button
                    onClick={() => setIsEditMode(!isEditMode)}
                    className={s.toggleEditBtn}
                    >
                    {isEditMode ? "Exit Edit Mode" : "Enter Edit Mode"}
                    </button>
                </div>

                {isEditMode && (
                    <button
                        onClick={handleBulkUpdate}
                        className={s.updateBtn}
                        disabled={isUpdating}
                        style={{ marginTop: '10px' }}
                    >
                        {isUpdating ? "Saving All Changes..." : "Save All Changes"}
                    </button>
                )}

                <div className={s.basicInfo}>
                <p><strong>Root Lemma：</strong>{detailData.baseJson.lemma}</p>
                <p><strong>All Parts of Speech：</strong>{detailData.baseJson.pos.join(" / ")}</p>
                </div>
            </div>

            {detailData.imageData && (
                <div className={s.topRight}>
                <img
                    src={detailData.imageData}
                    alt={`Word image for ${word}`}
                    className={s.wordImage}
                    onError={(e) => e.target.style.display = 'none'}
                />
                </div>
            )}
            </div>

            <div className={s.divLine}></div>

            <div className={s.posGridContainer}>
            {detailData.baseJson.pos.map((pos) => {
                const posContent = detailData.baseJson[pos];
                return (
                <div key={pos} className={s.posBlock}>
                    <h3 className={s.posTitle}>{pos.toUpperCase()}</h3>
                    {renderPosExtraFields(pos, posContent)}
                    <div className={s.meaningWrap}>
                    {renderMeanings(pos, posContent?.meanings)}
                    </div>
                </div>
                );
            })}
            </div>

            <div className={s.divLine}></div>

            <div className={s.lemmaTableWrap}>
            <h3>Word Variants & Pronunciation</h3>
            <table className={s.ipaTable}>
                <thead>
                <tr>
                    <th>Word Form</th>
                    <th>Relation</th>
                    <th>POS</th>
                    <th>US Spelling</th>
                    <th>UK IPA</th>
                    <th>US IPA</th>
                    <th>Action</th>
                </tr>
                </thead>
                <tbody>
                {detailData.lemmas.length === 0 ? (
                    <tr><td colSpan={7} className={s.emptyTip}>No variant data</td></tr>
                ) : (
                    detailData.lemmas.map((item, i) => {
                    const editedRow = editedLemmas[i] || {};
                    return (
                        <tr key={i}>
                        <td>{item.word}</td>
                        <td>{item.relationship}</td>
                        <td>{item.pos}</td>
                        <td>
                            {isEditMode ? (
                            <input
                                type="text"
                                className={s.tableInput}
                                value={editedRow.ame_spelling ?? item.ame_spelling ?? ""}
                                onChange={(e) => setEditedLemmas(prev => ({
                                ...prev,
                                [i]: { ...prev[i], ame_spelling: e.target.value }
                                }))}
                                disabled={isUpdating}
                            />
                            ) : (
                            item.ame_spelling || "-"
                            )}
                        </td>
                        <td>
                            {isEditMode ? (
                            <input
                                type="text"
                                className={s.tableInput}
                                value={editedRow.ipa_en_gb ?? item.ipa_en_gb ?? ""}
                                onChange={(e) => setEditedLemmas(prev => ({
                                ...prev,
                                [i]: { ...prev[i], ipa_en_gb: e.target.value }
                                }))}
                                disabled={isUpdating}
                            />
                            ) : (
                            item.ipa_en_gb || "-"
                            )}
                        </td>
                        <td>
                            {isEditMode ? (
                            <input
                                type="text"
                                className={s.tableInput}
                                value={editedRow.ipa_en_us ?? item.ipa_en_us ?? ""}
                                onChange={(e) => setEditedLemmas(prev => ({
                                ...prev,
                                [i]: { ...prev[i], ipa_en_us: e.target.value }
                                }))}
                                disabled={isUpdating}
                            />
                            ) : (
                            item.ipa_en_us || "-"
                            )}
                        </td>
                        <td>
                            {isEditMode && (
                            <button
                                onClick={() => handleUpdateVariant(i)}
                                className={s.variantUpdateBtn}
                                disabled={isUpdating}
                            >
                                {isUpdating ? "Saving..." : "Update"}
                            </button>
                            )}
                        </td>
                        </tr>
                    );
                    })
                )}
                </tbody>
            </table>
            </div>
        </div>
        )}
    </div>
    );
}